// SEQ-180 phase 2, task P13: one KPI card, extracted verbatim from
// PnlKpiCards.tsx so the widget renderer and the phase 1 row draw the same
// DOM. Nothing about the markup or the styles changed in the move: this is
// what makes the default dashboard's KPI row and today's KPI row the same
// pixels (plan section 8, P14).
//
// The optional `delta` is the only addition (P19, comparison). It renders
// nothing at all when absent, so a card without a comparison is byte-identical
// to the phase 1 card.
import { useEffect, useRef, type ReactNode, type CSSProperties } from "react";
import { animate, motion, useMotionValue, useReducedMotion, useTransform } from "framer-motion";
import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import { Money } from "../Money";
import { DELTA_COLOURS, type DeltaSentiment } from "../../features/pnlAnalytics/widgetPresentation";
import { CARD_TINT, cardTint } from "../../features/pnlAnalytics/cardTint";
import {
  countUpSeconds,
  EASE_OUT_EXPO,
  recountSeconds,
  PNL_COUNT_POP,
  PNL_COUNT_POP_SCALE,
  PNL_ENTRANCE_SPRING,
} from "../../features/pnlAnalytics/pnlMotion";

export interface PnlKpiDelta {
  /** Formatted change, already carrying its own unit, e.g. "+12.4%" or "$1.2M". */
  label: string;
  /** Which way the number moved. Chooses the ARROW only. */
  direction: "up" | "down" | "flat";
  /**
   * Whether that movement is good news. Chooses the COLOUR. Separate from
   * `direction` because overtime cost rising is not good news even though the
   * arrow points up (design audit, HIGH). Derived from the registry by
   * `deltaSentiment`, never from the sign.
   */
  sentiment: DeltaSentiment;
  /** What the change is measured against, e.g. "vs last month". */
  caption: string;
}

export interface PnlKpiCardProps {
  label: string;
  value: string;
  icon: ReactNode;
  color: string;
  delta?: PnlKpiDelta;
  /** Shown under the value for a `latest` metric, e.g. "as at Aug 2026" (plan section 6.3). */
  asAt?: string;
  /**
   * The raw number behind `value`. When both this and `formatValue` are given,
   * the card counts up to it once on mount instead of appearing at its final
   * figure.
   *
   * Optional, and the card is unchanged without it, which is what keeps the
   * phase 1 KPI row identical to the widget-rendered one (plan section 8, P14):
   * a caller that passes neither gets exactly the DOM it got before.
   */
  countTo?: number | null;
  /** Formats one frame of the count-up. Must be the same function that produced `value`. */
  formatValue?: (value: number) => string;
  /** SEQ-240: the "How is this worked out" trigger, placed beside the label. */
  info?: ReactNode;
}

/**
 * The counting number.
 *
 * A MotionValue, never useState: this writes roughly sixty frames a second and
 * a KPI row holds eight of these, so state updates would re-render the card
 * (and everything under it) 480 times a second on a page that also has to draw
 * eight charts. The MotionValue is written outside React and the string is
 * handed straight to the DOM node.
 */
function CountingValue({ to, format, final }: { to: number; format: (value: number) => string; final: string }) {
  const reduce = useReducedMotion() ?? false;
  const seconds = countUpSeconds(reduce);
  const progress = useMotionValue(seconds === 0 ? to : 0);
  const text = useTransform(progress, (frame) => format(frame));
  // The pop is a second MotionValue, not a re-mount. Re-mounting to replay a
  // keyframe would restart the count from zero and throw away the figure the
  // reader was already looking at.
  const pop = useMotionValue(1);
  const hasSettled = useRef(false);

  useEffect(() => {
    if (seconds === 0) {
      progress.set(to);
      hasSettled.current = true;
      return;
    }
    // FIRST PAINT COUNTS, A FILTER CHANGE POPS. Design audit: re-running the
    // full 0.9s count on every filter change churned every KPI for a second on
    // the page whose whole job is showing a number. A change now takes the
    // pop's own 0.35s, so the count and the pop are one gesture rather than a
    // short animation sitting inside a long one.
    const duration = hasSettled.current ? recountSeconds(reduce) : seconds;
    const counting = animate(progress, to, { duration, ease: [...EASE_OUT_EXPO] });
    // On the first run the number is already arriving with the card, so a pop
    // on top of it would be two animations saying one thing. Every run after
    // that is a filter change, which is precisely when the app's own
    // `dfb-count-pop` fires, so the same curve fires here.
    const popping = hasSettled.current
      ? animate(pop, [...PNL_COUNT_POP_SCALE], { duration: PNL_COUNT_POP.duration, ease: [...PNL_COUNT_POP.ease] })
      : null;
    hasSettled.current = true;
    return () => {
      counting.stop();
      popping?.stop();
    };
  }, [progress, pop, to, seconds, reduce]);

  // The final string is in the DOM for assistive technology and for anyone
  // reading the page before the count finishes; the animated span is hidden
  // from the accessibility tree so a screen reader is not read a running
  // number.
  return (
    <>
      <span className="pnl-visually-hidden">{final}</span>
      <motion.span aria-hidden="true" style={{ display: "inline-block", scale: pop }}>
        {text}
      </motion.span>
    </>
  );
}

export function PnlKpiCard({ label, value, icon, color, delta, asAt, countTo, formatValue, info }: PnlKpiCardProps) {
  const reduce = useReducedMotion() ?? false;
  // A DEFECT THE MOTION PASS MADE VISIBLE. These tints were hex-alpha suffixes
  // (`${color}0d`), which only work when `color` is a six-digit hex. The
  // renderer's fallback presentation is `var(--seq-blue)`, a token, so every
  // KPI a user built on a metric outside the eight preset ones produced
  // `var(--seq-blue)0d`: invalid CSS, silently dropped, leaving that card with
  // no background and no border while its neighbours had both.
  //
  // color-mix takes either form, so the presets render as before (0d is 5.1%)
  // and a token-coloured card is finally tinted at all.
  const cardStyle: CSSProperties = {
    padding: "1.1rem",
    background: `linear-gradient(135deg, ${cardTint(color, CARD_TINT.surface)} 0%, ${cardTint(color, CARD_TINT.surfaceFade)} 100%)`,
    border: `1px solid ${cardTint(color, CARD_TINT.edge)}`,
    borderRadius: 14,
  };

  const counts = typeof countTo === "number" && Number.isFinite(countTo) && formatValue !== undefined;

  // NO HOVER LIFT. Design audit: this card had `whileHover={{ y: -2 }}` and
  // nothing else. It is not clickable, has no href and takes no focus, so the
  // lift was a promise of interactivity the card cannot keep. The chart cards
  // keep theirs because their bars drill down, which is what makes the same
  // gesture honest there.
  return (
    <div className="card pnl-kpi-card" style={cardStyle}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <span className="hwo-heading">
          <span style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.03em" }}>
            {label}
          </span>
          {info}
        </span>
        <div style={{ width: 30, height: 30, borderRadius: 8, background: cardTint(color, CARD_TINT.icon), display: "flex", alignItems: "center", justifyContent: "center", color }}>
          {icon}
        </div>
      </div>
      <Money as="div" style={{ fontSize: "1.3rem", fontWeight: 700, color: "var(--text-primary)" }}>
        {counts ? <CountingValue to={countTo as number} format={formatValue as (v: number) => string} final={value} /> : value}
      </Money>
      {asAt && (
        <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)", marginTop: 4 }}>{asAt}</div>
      )}
      {delta && (
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={PNL_ENTRANCE_SPRING}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            marginTop: 8,
            fontSize: "0.75rem",
            fontWeight: 600,
            color: DELTA_COLOURS[delta.sentiment],
          }}
        >
          {delta.direction === "up" ? (
            <ArrowUpRight size={13} aria-hidden="true" />
          ) : delta.direction === "down" ? (
            <ArrowDownRight size={13} aria-hidden="true" />
          ) : (
            <Minus size={13} aria-hidden="true" />
          )}
          <span data-seq-private>{delta.label}</span>
          <span style={{ color: "var(--text-secondary)", fontWeight: 500 }}>{delta.caption}</span>
        </motion.div>
      )}
    </div>
  );
}
